import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { KeyRound, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NuxxumLogo, NuxxumWordmark } from "@/components/NuxxumLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "h-11 bg-white/[0.05] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#766EFF]/60 focus-visible:ring-offset-0";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [checking, setChecking] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/auth/reset-password/${encodeURIComponent(token)}/valid`);
        if (!cancelled) setValidToken(!!data.valid);
      } catch {
        if (!cancelled) setValidToken(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Las contraseñas no coinciden");
      return;
    }
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      setUser(data);
      toast.success("Contraseña actualizada. ¡Bienvenido de vuelta!");
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#171927] text-white overflow-x-hidden flex flex-col" data-testid="reset-password-page">
      <div className="pointer-events-none fixed inset-0 opacity-60"
        style={{ background: "radial-gradient(ellipse at 15% 10%, #5B52EB26 0%, transparent 45%), radial-gradient(ellipse at 85% 90%, #7AD7C414 0%, transparent 45%)" }} />
      <header className="relative max-w-7xl w-full mx-auto px-6 lg:px-10 h-20 flex items-center gap-3">
        <NuxxumLogo size={38} />
        <NuxxumWordmark className="text-2xl text-white" />
      </header>
      <main className="relative flex-1 flex items-start justify-center px-6 pt-8 pb-16">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-7 sm:p-8 space-y-6 shadow-2xl shadow-black/40">
            {checking ? (
              <div className="text-center py-8" data-testid="reset-checking">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#9A94FF]" />
                <p className="text-sm text-white/50 mt-3">Verificando el enlace…</p>
              </div>
            ) : !validToken ? (
              <div className="text-center space-y-4" data-testid="reset-invalid">
                <div className="mx-auto h-14 w-14 rounded-full bg-red-500/10 flex items-center justify-center">
                  <XCircle className="h-8 w-8 text-red-400" />
                </div>
                <h2 className="font-heading text-2xl font-bold">Enlace no válido</h2>
                <p className="text-sm text-white/60">
                  Este enlace ha caducado, ya se ha usado o no es correcto. Solicita uno nuevo desde la página de inicio de sesión.
                </p>
                <div className="flex gap-2 justify-center pt-2">
                  <Link to="/forgot-password">
                    <Button className="bg-gradient-to-r from-[#5B52EB] to-[#766EFF] border-0" data-testid="reset-request-new-btn">
                      Solicitar nuevo enlace
                    </Button>
                  </Link>
                  <Link to="/auth">
                    <Button variant="outline" className="border-white/20 bg-transparent hover:bg-white/10">
                      Ir a iniciar sesión
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <div className="h-12 w-12 rounded-xl bg-[#5B52EB]/15 flex items-center justify-center mb-4">
                    <KeyRound className="h-5 w-5 text-[#9A94FF]" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold tracking-tight">Elige una contraseña nueva</h2>
                  <p className="text-white/50 mt-1 text-sm">
                    Debe tener al menos 6 caracteres. Después iniciarás sesión automáticamente.
                  </p>
                </div>
                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="new-password" className="text-white/70 text-xs">Nueva contraseña</Label>
                    <Input
                      id="new-password"
                      data-testid="reset-new-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 6 caracteres"
                      required
                      minLength={6}
                      className={inputCls}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-password" className="text-white/70 text-xs">Confirma la contraseña</Label>
                    <Input
                      id="confirm-password"
                      data-testid="reset-confirm-password-input"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="Repite la contraseña"
                      required
                      minLength={6}
                      className={inputCls}
                    />
                  </div>
                  {error && <p className="text-sm text-red-400" data-testid="reset-error">{error}</p>}
                  <Button
                    type="submit"
                    data-testid="reset-submit-btn"
                    disabled={loading}
                    className="w-full h-11 text-sm font-semibold bg-gradient-to-r from-[#5B52EB] to-[#766EFF] hover:opacity-90 transition-opacity border-0 text-white"
                  >
                    {loading ? "Guardando…" : (<><CheckCircle2 className="h-4 w-4 mr-2" /> Cambiar contraseña</>)}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
